import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Zap,
  Flame,
  AlertTriangle,
  Ruler,
  Droplets,
  FileSpreadsheet,
  Home,
  FileCheck,
  ClipboardCheck,
  Clock,
  Phone
} from 'lucide-react';

const Services = () => {
  const services = [
    {
      icon: <FileSpreadsheet className="h-12 w-12 text-dk-blue" />,
      title: "DPE & Audit énergétique",
      description: "Évaluation précise de la performance énergétique de votre bien et recommandations d'amélioration."
    },
    {
      icon: <AlertTriangle className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Amiante",
      description: "Détection et analyse des matériaux susceptibles de contenir de l'amiante dans votre bien."
    },
    {
      icon: <Zap className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Électricité",
      description: "Vérification complète de l'installation électrique pour garantir la sécurité des occupants."
    },
    {
      icon: <Flame className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Gaz",
      description: "Contrôle des installations de gaz pour prévenir tout risque d'accident."
    },
    {
      icon: <Home className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Termites",
      description: "Inspection approfondie pour détecter la présence de termites et autres insectes xylophages."
    },
    {
      icon: <Ruler className="h-12 w-12 text-dk-blue" />,
      title: "Mesurages Carrez / Boutin",
      description: "Mesure précise de la surface habitable selon les lois Carrez et Boutin pour les transactions immobilières."
    },
    {
      icon: <Droplets className="h-12 w-12 text-dk-blue" />,
      title: "Diagnostic Plomb",
      description: "Recherche de la présence de plomb dans les peintures pour la protection des occupants."
    },
    {
      icon: <FileCheck className="h-12 w-12 text-dk-blue" />,
      title: "État des Risques (ERP)",
      description: "Information sur les risques naturels, miniers et technologiques affectant votre bien."
    }
  ];

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.5 }
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.5 }}
      className="pt-32 pb-16"
    >
      <div className="container mx-auto px-4">
        <div className="text-center mb-12">
          <h1 className="text-3xl md:text-4xl font-bold mb-6 text-gray-900">Nos Services de Diagnostics Immobiliers</h1>
          <p className="text-lg text-gray-600 max-w-3xl mx-auto">
            Notre diagnostiqueur certifié se déplace à la demande des propriétaires, des bailleurs 
            et des différents acteurs de l'immobilier pour réaliser tous les diagnostics obligatoires 
            pour la vente et la location de biens.
          </p>
        </div>

        {/* Grille des services */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-16"
        >
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={itemVariants}
              className="bg-gradient-to-br from-white to-gray-50 rounded-lg shadow-lg p-8 hover:shadow-xl transition-all duration-300 border-l-4 border-transparent hover:border-dk-blue group"
            >
              <div className="mb-6 transform group-hover:scale-110 transition-transform duration-300">
                {service.icon}
              </div>
              <h3 className="text-xl font-semibold mb-4 text-gray-900 group-hover:text-dk-blue transition-colors">
                {service.title}
              </h3>
              <p className="text-gray-600 leading-relaxed">
                {service.description}
              </p>
            </motion.div>
          ))}
        </motion.div>

        {/* Section Notre Force */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="bg-gradient-to-br from-white to-gray-100 rounded-xl p-8 shadow-lg mb-16 border border-gray-200"
        >
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div>
              <h2 className="text-2xl font-bold mb-4 text-gray-900">Notre Force ?</h2>
              <div className="flex items-start space-x-4 mb-4">
                <Clock className="h-6 w-6 flex-shrink-0 mt-1 text-dk-blue" />
                <p className="text-lg text-gray-700">
                  Disponibilité et réactivité pour tous vos projets immobiliers.
                </p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <ClipboardCheck className="h-16 w-16 text-dk-blue" />
              <div className="text-right">
                <p className="text-3xl font-bold text-gray-900">100%</p>
                <p className="text-gray-600">Satisfaction client</p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Call to Action */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center"
        >
          <h2 className="text-2xl font-bold mb-6 text-gray-900">Besoin d'un diagnostic ?</h2>
          <p className="text-lg text-gray-600 mb-8">
            Contactez-nous pour obtenir un devis personnalisé ou prendre rendez-vous.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <a 
              href="tel:0789307500" 
              className="btn-primary flex items-center justify-center hover:bg-dk-blue/90 transition-colors"
            >
              <Phone className="h-5 w-5 mr-2" />
              Nous appeler
            </a>
            <Link 
              to="/#devis"
              className="btn bg-white text-dk-blue border-2 border-dk-blue hover:bg-dk-blue hover:text-white transition-colors"
            >
              Demander un devis
            </Link>
          </div>
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Services;